import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  api,
  ApiError,
  getAssetBlobUrl,
  type Campaign,
  type CampaignSuggestion,
  type SocialProvider,
  type StockPhoto,
} from "../../lib/api";
import StockImagePicker from "../../components/StockImagePicker";

const OBJECTIVES = [
  "awareness",
  "engagement",
  "lead_generation",
  "conversion",
  "retention",
] as const;
const OBJECTIVE_LABELS: Record<string, string> = {
  awareness: "Awareness",
  engagement: "Engagement",
  lead_generation: "Lead Generation",
  conversion: "Conversion",
  retention: "Retention",
};

const PLATFORMS: { id: string; label: string; provider: SocialProvider }[] = [
  { id: "instagram_feed", label: "Instagram Feed", provider: "meta_instagram" },
  {
    id: "instagram_story",
    label: "Instagram Story",
    provider: "meta_instagram",
  },
  { id: "linkedin", label: "LinkedIn", provider: "linkedin" },
  { id: "twitter", label: "X / Twitter", provider: "x" },
  { id: "facebook", label: "Facebook", provider: "meta_facebook" },
];

// Which top-level campaign fields can be AI-suggested
type SuggestedField = "name" | "objective" | "product" | "keyMessages";

export default function CampaignEditor() {
  const { brandId, campaignId } = useParams<{
    brandId: string;
    campaignId: string;
  }>();
  const navigate = useNavigate();
  const isNew = !campaignId;

  const [form, setForm] = useState<Partial<Campaign>>({
    name: "",
    objective: "awareness",
    platforms: ["instagram_feed"],
    keyMessages: [""],
    startDate: new Date().toISOString().slice(0, 10),
    endDate: "",
    product: { name: "", description: "", differentiators: [] },
    assetR2Keys: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── AI analyzer state ──────────────────────────────────────────────────────
  const [analyzeUrl, setAnalyzeUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [siteBlocked, setSiteBlocked] = useState(false);
  const [pastedText, setPastedText] = useState("");
  const [suggestion, setSuggestion] = useState<CampaignSuggestion | null>(null);
  const [suggestedFields, setSuggestedFields] = useState<Set<SuggestedField>>(
    new Set(),
  );
  const preApplySnapshot = useRef<Partial<Campaign> | null>(null);

  // ── Image state ────────────────────────────────────────────────────────────
  const [suggestedImageUrls, setSuggestedImageUrls] = useState<string[]>([]);
  const [importingImageUrl, setImportingImageUrl] = useState<string | null>(
    null,
  );
  const [importedImageUrls, setImportedImageUrls] = useState<Set<string>>(
    new Set(),
  );
  // URL → R2Key mapping for imported scraped images
  const [importedImageToR2Key, setImportedImageToR2Key] = useState<
    Record<string, string>
  >({});
  // Which scraped image URL is selected as the campaign's primary image
  const [selectedScrapeUrl, setSelectedScrapeUrl] = useState<string | null>(
    null,
  );
  // Blob URLs for existing campaign assets (R2Key → blob URL)
  const [assetBlobUrls, setAssetBlobUrls] = useState<Record<string, string>>(
    {},
  );
  // Upload ref for existing assets section
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  // Stock image picker modal
  const [showStockPicker, setShowStockPicker] = useState(false);

  // Which social providers have active connections for this brand
  const [connectedProviders, setConnectedProviders] = useState<
    Set<SocialProvider>
  >(new Set());

  useEffect(() => {
    // Load connected social accounts for the brand so the platform picker
    // can warn when a selected platform has no account connected.
    if (brandId) {
      api.social
        .list(brandId)
        .then(({ connections }) =>
          setConnectedProviders(
            new Set(connections.map((c) => c.provider as SocialProvider)),
          ),
        )
        .catch(() => {
          /* non-fatal */
        });
    }
  }, [brandId]);

  useEffect(() => {
    if (!isNew && brandId && campaignId) {
      api.campaigns
        .get(brandId, campaignId)
        .then((r) => {
          setForm(r.campaign);
          // Restore the source URL into the analyzer input
          if (r.campaign.sourceUrl) setAnalyzeUrl(r.campaign.sourceUrl);
          // Load blob URLs for existing assets
          for (const r2Key of r.campaign.assetR2Keys ?? []) {
            getAssetBlobUrl(r2Key)
              .then((url) =>
                setAssetBlobUrls((prev) => ({ ...prev, [r2Key]: url })),
              )
              .catch(() => {
                /* non-fatal — blob URL will remain missing */
              });
          }
        })
        .catch(console.error);
    }
  }, [brandId, campaignId, isNew]);

  const set = (key: keyof Campaign, val: unknown) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const togglePlatform = (id: string) =>
    set(
      "platforms",
      form.platforms?.includes(id as never)
        ? form.platforms.filter((p) => p !== id)
        : [...(form.platforms ?? []), id],
    );

  const setMessage = (i: number, val: string) => {
    const msgs = [...(form.keyMessages ?? [])];
    msgs[i] = val;
    set("keyMessages", msgs);
  };

  // ── AI suggestion helpers ──────────────────────────────────────────────────

  const applySuggestion = (s: CampaignSuggestion) => {
    preApplySnapshot.current = { ...form };
    const applied = new Set<SuggestedField>();

    setForm((prev) => {
      const next = { ...prev };
      if (s.name) {
        next.name = s.name;
        applied.add("name");
      }
      if (s.objective) {
        next.objective = s.objective as Campaign["objective"];
        applied.add("objective");
      }
      if (s.product) {
        next.product = {
          name: s.product.name ?? prev.product?.name ?? "",
          description: s.product.description ?? prev.product?.description ?? "",
          pricePoint: s.product.pricePoint ?? prev.product?.pricePoint,
          differentiators:
            s.product.differentiators ?? prev.product?.differentiators ?? [],
        };
        applied.add("product");
      }
      if (s.keyMessages?.length) {
        next.keyMessages = s.keyMessages;
        applied.add("keyMessages");
      }
      return next;
    });

    setSuggestedFields(applied);
    setSuggestion(s);
  };

  const handleUndoSuggestions = () => {
    if (!preApplySnapshot.current) return;
    setForm(preApplySnapshot.current);
    preApplySnapshot.current = null;
    setSuggestion(null);
    setSuggestedFields(new Set());
  };

  const handleClearSuggestions = () => {
    setSuggestion(null);
    setSuggestedFields(new Set());
    preApplySnapshot.current = null;
  };

  const clearSuggestedField = (field: SuggestedField) =>
    setSuggestedFields((prev) => {
      const n = new Set(prev);
      n.delete(field);
      return n;
    });

  // ── AI analyze handlers ────────────────────────────────────────────────────

  const handleAnalyzeUrl = async () => {
    const url = analyzeUrl.trim();
    if (!url || !brandId) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setSuggestion(null);
    setSiteBlocked(false);
    setSuggestedImageUrls([]);
    setSelectedScrapeUrl(null);

    try {
      const res = await api.campaigns.analyzeUrl(brandId, url);
      applySuggestion(res.suggestion);
      const rawImgs =
        res.suggestedImageUrls ?? res.suggestion.suggestedImageUrls ?? [];
      // Deduplicate by normalized URL (origin + pathname), so images that differ
      // only in query params (e.g. ?w=1200 vs bare path) are treated as one.
      const seenPaths = new Set<string>();
      const imgs = rawImgs.filter((url) => {
        try {
          const u = new URL(url);
          const key = `${u.origin}${u.pathname}`;
          if (seenPaths.has(key)) return false;
          seenPaths.add(key);
          return true;
        } catch {
          return !seenPaths.has(url) && (seenPaths.add(url), true);
        }
      });
      if (imgs.length > 0) setSuggestedImageUrls(imgs);
      // Persist the URL so it's saved with the campaign
      setForm((prev) => ({ ...prev, sourceUrl: url }));
    } catch (e: unknown) {
      const isBlocked = e instanceof ApiError && e.status === 403;
      if (isBlocked) {
        setSiteBlocked(true);
        setAnalyzeError(
          "This site blocked automated access. Copy some text from the page and paste it below — the AI will work from that instead.",
        );
      } else {
        setAnalyzeError(e instanceof Error ? e.message : "Analysis failed");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeText = async () => {
    if (!pastedText.trim() || !brandId) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setSuggestion(null);

    try {
      const res = await api.campaigns.analyzeText(brandId, pastedText);
      applySuggestion(res.suggestion);
      setSiteBlocked(false);
    } catch (e) {
      setAnalyzeError(e instanceof Error ? e.message : "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Image handlers ─────────────────────────────────────────────────────────

  const handleImportImage = async (imageUrl: string, makePrimary = false) => {
    if (!brandId || !campaignId || importingImageUrl) return;
    setImportingImageUrl(imageUrl);
    try {
      const { r2Key } = await api.campaigns.importImageFromUrl(
        brandId,
        campaignId,
        imageUrl,
      );
      setImportedImageUrls((prev) => new Set([...prev, imageUrl]));
      setImportedImageToR2Key((prev) => ({ ...prev, [imageUrl]: r2Key }));
      setForm((prev) => ({
        ...prev,
        assetR2Keys: makePrimary
          ? [r2Key, ...(prev.assetR2Keys ?? []).filter((k) => k !== r2Key)]
          : [...(prev.assetR2Keys ?? []).filter((k) => k !== r2Key), r2Key],
      }));
      if (makePrimary) setSelectedScrapeUrl(imageUrl);
      // Load blob URL for the imported image to show in assets panel
      getAssetBlobUrl(r2Key)
        .then((url) => setAssetBlobUrls((prev) => ({ ...prev, [r2Key]: url })))
        .catch(() => {
          /* non-fatal */
        });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to import image");
    } finally {
      setImportingImageUrl(null);
    }
  };

  const handleSelectScrapeImage = async (imgUrl: string) => {
    // Toggle off if already selected
    if (selectedScrapeUrl === imgUrl) {
      setSelectedScrapeUrl(null);
      return;
    }

    if (!campaignId) {
      // New campaign — just mark visually; auto-import on save
      setSelectedScrapeUrl(imgUrl);
      return;
    }

    // Existing campaign — import (if needed) and set as primary
    if (!importedImageUrls.has(imgUrl)) {
      await handleImportImage(imgUrl, true);
    } else {
      // Already imported — just move its R2Key to front
      const r2Key = importedImageToR2Key[imgUrl];
      if (r2Key) {
        setForm((prev) => ({
          ...prev,
          assetR2Keys: [
            r2Key,
            ...(prev.assetR2Keys ?? []).filter((k) => k !== r2Key),
          ],
        }));
      }
      setSelectedScrapeUrl(imgUrl);
    }
  };

  const handleStockImageImported = (r2Key: string, _photo: StockPhoto) => {
    setForm((prev) => ({
      ...prev,
      assetR2Keys: [
        ...(prev.assetR2Keys ?? []).filter((k) => k !== r2Key),
        r2Key,
      ],
    }));
    getAssetBlobUrl(r2Key)
      .then((url) => setAssetBlobUrls((prev) => ({ ...prev, [r2Key]: url })))
      .catch(() => {
        /* non-fatal */
      });
  };

  const handleSetPrimaryAsset = (r2Key: string) => {
    setForm((prev) => ({
      ...prev,
      assetR2Keys: [
        r2Key,
        ...(prev.assetR2Keys ?? []).filter((k) => k !== r2Key),
      ],
    }));
  };

  const handleUploadCampaignAsset = async (file: File) => {
    if (!brandId || !campaignId) return;
    setUploadingAsset(true);
    try {
      const { r2Key } = await api.campaigns.uploadImage(
        brandId,
        campaignId,
        file,
      );
      setForm((prev) => ({
        ...prev,
        assetR2Keys: [
          ...(prev.assetR2Keys ?? []).filter((k) => k !== r2Key),
          r2Key,
        ],
      }));
      getAssetBlobUrl(r2Key)
        .then((url) => setAssetBlobUrls((prev) => ({ ...prev, [r2Key]: url })))
        .catch(() => {
          /* non-fatal */
        });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploadingAsset(false);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.name?.trim()) return setError("Campaign name is required");
    if (!brandId) return;
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const payload: Partial<Campaign> = {
          ...form,
          sourceUrl: analyzeUrl.trim() || undefined,
        };
        const { campaign: created } = await api.campaigns.create(
          brandId,
          payload,
        );
        // Auto-import the pre-selected scraped image as the primary asset
        if (selectedScrapeUrl) {
          try {
            await api.campaigns.importImageFromUrl(
              brandId,
              created.campaignId,
              selectedScrapeUrl,
            );
          } catch {
            // Non-fatal — campaign still created, image import just failed
          }
        }
      } else {
        await api.campaigns.update(brandId, campaignId!, {
          ...form,
          sourceUrl: analyzeUrl.trim() || undefined,
        });
      }
      navigate(`/brands/${brandId}/campaigns`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasSuggestions = suggestedFields.size > 0;
  const suggestedRing = "ring-2 ring-brand-200 border-brand-300";
  const primaryAssetKey = (form.assetR2Keys ?? [])[0] ?? null;
  const hasExistingAssets = !isNew && (form.assetR2Keys?.length ?? 0) > 0;

  return (
    <div className="max-w-3xl">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/brands/${brandId}/campaigns`)}
          aria-label="Back to campaigns"
          className="text-faint hover:text-slate text-lg"
        >
          ←
        </button>
        <div className="flex-1">
          <h1 className="page-title">
            {isNew ? "New campaign" : "Edit campaign"}
          </h1>
          {isNew && (
            <p className="text-sm text-muted mt-0.5">
              Define what the AI should write about for this brand.
            </p>
          )}
        </div>
        {isNew && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-faint flex-shrink-0">
            <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
              ✓
            </span>
            <span className="text-faint">Brand</span>
            <span>→</span>
            <span className="bg-brand-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-semibold">
              2
            </span>
            <span className="font-medium text-slate">Campaign</span>
            <span>→</span>
            <span className="rounded-full w-5 h-5 flex items-center justify-center border border-gray-300 font-semibold">
              3
            </span>
            <span>Generate</span>
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 mb-4">
          {error}
        </p>
      )}

      {/* ── AI Campaign Analyzer ── */}
      <div className="card p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-base">✦</span>
          <p className="text-sm font-semibold text-ink">AI campaign analyzer</p>
          <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full ml-auto">
            AI
          </span>
        </div>
        <p className="text-xs text-muted mb-3">
          Paste a product page, landing page, or offer URL — the AI will suggest
          a campaign name, objective, product details, and key messages.
        </p>

        <div className="flex gap-2">
          <input
            type="url"
            className="input flex-1 text-sm"
            value={analyzeUrl}
            onChange={(e) => setAnalyzeUrl(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && !analyzing && handleAnalyzeUrl()
            }
            placeholder="https://yoursite.com/product"
            aria-label="Product or campaign page URL to analyze"
          />
          <button
            onClick={handleAnalyzeUrl}
            disabled={analyzing || !analyzeUrl.trim()}
            aria-label="Analyze URL"
            className="btn-primary text-sm px-4 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {analyzing ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                Analyzing…
              </span>
            ) : (
              "Analyze"
            )}
          </button>
        </div>

        {analyzeError && (
          <div className="mt-3">
            <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {analyzeError}
            </p>
            {siteBlocked && (
              <div className="mt-3">
                <p className="text-xs text-muted mb-1.5">
                  Paste text from the page (copy from your browser, paste here):
                </p>
                <textarea
                  className="input text-xs min-h-[100px] resize-y"
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder="Paste product copy, landing page text, offer details…"
                  aria-label="Paste page text for AI analysis"
                />
                <button
                  onClick={handleAnalyzeText}
                  disabled={analyzing || !pastedText.trim()}
                  className="btn-primary text-xs mt-2 disabled:opacity-50"
                >
                  {analyzing ? "Analyzing…" : "Analyze pasted text"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Suggestion rationale + regenerate / undo */}
        {hasSuggestions && suggestion && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate mb-0.5">
                  AI filled in the form
                </p>
                <p className="text-xs text-muted leading-relaxed">
                  {suggestion.sourceNotes ??
                    "Fields were inferred from the page content. Review and adjust before saving."}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {Array.from(suggestedFields).map((field) => (
                    <span
                      key={field}
                      className="text-xs bg-brand-50 text-brand-700 border border-brand-100 px-2 py-0.5 rounded-full capitalize"
                    >
                      {field === "keyMessages"
                        ? "Key messages"
                        : field === "product"
                          ? "Product details"
                          : field}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 items-start">
                {analyzeUrl.trim() && (
                  <button
                    onClick={handleAnalyzeUrl}
                    disabled={analyzing}
                    aria-label="Regenerate AI suggestions from the same URL"
                    title="Regenerate AI suggestions"
                    className="text-xs text-brand-600 hover:text-brand-800 border border-brand-200 hover:border-brand-400 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {analyzing ? "…" : "↺ Regenerate"}
                  </button>
                )}
                {preApplySnapshot.current && (
                  <button
                    onClick={handleUndoSuggestions}
                    className="text-xs text-muted hover:text-slate underline"
                  >
                    Undo
                  </button>
                )}
                <button
                  onClick={handleClearSuggestions}
                  className="text-xs text-faint hover:text-slate underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Scraped images from URL ── */}
        {suggestedImageUrls.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-semibold text-slate">
                  Images found on this page
                </p>
                <p className="text-xs text-faint mt-0.5">
                  {isNew
                    ? "Click an image to select it as your campaign's primary image — it will be imported when you save."
                    : "Click an image to import it and set it as the primary image for this campaign's posts."}
                </p>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {suggestedImageUrls.map((imgUrl) => {
                const isSelected = selectedScrapeUrl === imgUrl;
                const isImported = importedImageUrls.has(imgUrl);
                const isImporting = importingImageUrl === imgUrl;
                return (
                  <button
                    key={imgUrl}
                    onClick={() => handleSelectScrapeImage(imgUrl)}
                    disabled={!!importingImageUrl}
                    aria-pressed={isSelected}
                    aria-label={
                      isSelected
                        ? "Deselect this image"
                        : "Select this image as campaign primary"
                    }
                    className={`relative flex-shrink-0 rounded-xl overflow-hidden transition-all focus:outline-none ${
                      isSelected
                        ? "ring-2 ring-brand-500 ring-offset-2"
                        : "ring-1 ring-gray-200 hover:ring-brand-300"
                    } disabled:cursor-wait`}
                  >
                    <img
                      src={imgUrl}
                      alt="Suggested campaign image"
                      className="w-24 h-24 object-cover bg-gray-100 block"
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        // Hide images that are clearly UI elements once their
                        // actual dimensions are known — catches spinners and
                        // icons that lack explicit HTML size attributes.
                        if (img.naturalWidth < 80 || img.naturalHeight < 80) {
                          const wrapper = img.closest("button");
                          if (wrapper) wrapper.style.display = "none";
                        }
                      }}
                      onError={(e) => {
                        const wrapper = (
                          e.currentTarget as HTMLImageElement
                        ).closest("button");
                        if (wrapper) wrapper.style.display = "none";
                      }}
                    />
                    {isImporting && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      </div>
                    )}
                    {isSelected && !isImporting && (
                      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-center">
                        <span className="text-xs font-semibold bg-brand-500 text-white px-1.5 py-0.5 rounded-full">
                          {isImported ? "Primary" : "Selected"}
                        </span>
                      </div>
                    )}
                    {!isSelected && isImported && !isImporting && (
                      <div className="absolute top-1 right-1">
                        <span className="text-xs bg-green-500 text-white px-1 py-0.5 rounded-full">
                          ✓
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedScrapeUrl && isNew && (
              <p className="text-xs text-brand-700 mt-2 flex items-center gap-1">
                <span>✦</span>
                Selected image will be imported as the primary campaign image
                when you save.
              </p>
            )}
            {selectedScrapeUrl && !isNew && (
              <p className="text-xs text-green-700 mt-2">
                Primary image updated — save to confirm.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* ── Basics ── */}
        <div className="card p-6 space-y-4">
          <h2 className="eyebrow">Basics</h2>

          <div>
            <label className="label">
              Campaign name <span className="text-spark">*</span>
            </label>
            <input
              className={`input ${suggestedFields.has("name") ? suggestedRing : ""}`}
              value={form.name ?? ""}
              onChange={(e) => {
                set("name", e.target.value);
                clearSuggestedField("name");
              }}
              placeholder="Summer Sale 2026"
            />
            {suggestedFields.has("name") && (
              <p className="text-xs text-brand-600 mt-1">✦ Suggested by AI</p>
            )}
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="label">
                Objective
                {suggestedFields.has("objective") && (
                  <span className="ml-2 text-xs text-brand-600 font-normal">
                    ✦ AI suggested
                  </span>
                )}
              </label>
              <select
                className={`input capitalize ${suggestedFields.has("objective") ? suggestedRing : ""}`}
                value={form.objective ?? "awareness"}
                onChange={(e) => {
                  set("objective", e.target.value);
                  clearSuggestedField("objective");
                }}
              >
                {OBJECTIVES.map((o) => (
                  <option key={o} value={o}>
                    {OBJECTIVE_LABELS[o]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="label">Start date</label>
              <input
                type="date"
                className="input"
                value={form.startDate ?? ""}
                onChange={(e) => set("startDate", e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="label">End date</label>
              <input
                type="date"
                className="input"
                value={form.endDate ?? ""}
                onChange={(e) => set("endDate", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="label">Primary link / CTA URL</label>
            <input
              type="url"
              className="input"
              placeholder={
                form.sourceUrl
                  ? `Defaults to analyzed URL: ${form.sourceUrl}`
                  : "https://example.com/product"
              }
              value={form.ctas?.[0]?.url ?? ""}
              onChange={(e) => {
                const url = e.target.value.trim();
                set(
                  "ctas",
                  url
                    ? [
                        {
                          id: form.ctas?.[0]?.id ?? "primary",
                          label: form.ctas?.[0]?.label ?? "Primary CTA",
                          url,
                        },
                      ]
                    : [],
                );
              }}
            />
            <p className="text-xs text-faint">
              Used for link backs and Open Graph metadata. Leave blank to use
              the analyzed URL instead.
            </p>
          </div>

          <div className="flex items-start justify-between gap-4 pt-1">
            <div className="flex-1">
              <p className="text-sm font-medium text-slate">
                Include link backs in post content
              </p>
              <p className="text-xs text-faint mt-0.5 leading-relaxed">
                When enabled, the AI will embed the primary link URL directly in
                the post copy — e.g. a product post will link back to the
                product page.
              </p>
            </div>
            <button
              role="switch"
              aria-checked={form.includeLinkBacks ?? false}
              aria-label="Toggle link backs in post content"
              onClick={() =>
                set("includeLinkBacks", !(form.includeLinkBacks ?? false))
              }
              className={`relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                form.includeLinkBacks ? "bg-brand-500" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  form.includeLinkBacks ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        {/* ── Campaign Images ── */}
        {!isNew && (
          <div className="card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="eyebrow">Campaign Images</h2>
                <p className="text-xs text-faint mt-0.5">
                  The primary image (first) is used by the AI for post
                  generation. Click any image to set it as primary. Upload to
                  add more.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowStockPicker(true)}
                  className="text-xs text-brand-600 hover:text-brand-800 underline"
                >
                  Browse stock photos
                </button>
                <label
                  aria-label="Upload campaign image"
                  className={`text-xs text-brand-600 hover:text-brand-800 cursor-pointer underline ${uploadingAsset ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {uploadingAsset ? "Uploading…" : "+ Upload image"}
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadCampaignAsset(file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            </div>

            {!hasExistingAssets && !uploadingAsset && (
              <p className="text-xs text-faint italic">
                No images yet. Upload one or select from the AI analyzer above.
                If no image is set, the AI will generate posts without a
                specific image recommendation.
              </p>
            )}

            {(form.assetR2Keys ?? []).length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {(form.assetR2Keys ?? []).map((r2Key, idx) => {
                  const isPrimary = idx === 0;
                  const blobUrl = assetBlobUrls[r2Key];
                  return (
                    <button
                      key={r2Key}
                      onClick={() => !isPrimary && handleSetPrimaryAsset(r2Key)}
                      disabled={isPrimary}
                      aria-label={
                        isPrimary
                          ? "Primary campaign image"
                          : "Set as primary campaign image"
                      }
                      title={
                        isPrimary ? "Primary image" : "Click to set as primary"
                      }
                      className={`relative flex-shrink-0 rounded-xl overflow-hidden transition-all focus:outline-none ${
                        isPrimary
                          ? "ring-2 ring-brand-500 ring-offset-1 cursor-default"
                          : "ring-1 ring-gray-200 hover:ring-brand-300 cursor-pointer"
                      }`}
                    >
                      {blobUrl ? (
                        <img
                          src={blobUrl}
                          alt={
                            isPrimary
                              ? "Primary campaign image"
                              : "Campaign image"
                          }
                          className="w-20 h-20 object-cover block"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-300 text-xs">
                            Loading…
                          </span>
                        </div>
                      )}
                      {isPrimary && (
                        <div className="absolute bottom-1 left-1 right-1 flex justify-center">
                          <span className="text-xs font-semibold bg-brand-500 text-white px-1.5 py-0.5 rounded-full">
                            Primary
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {!primaryAssetKey && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                No primary image set. The AI will choose an appropriate image
                during post generation, or posts will be text-only.
              </p>
            )}
          </div>
        )}

        {/* ── Product ── */}
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="eyebrow">Product / Service</h2>
            {suggestedFields.has("product") && (
              <span className="text-xs text-brand-600">✦ AI suggested</span>
            )}
          </div>

          <div>
            <label className="label">Product / service name</label>
            <input
              className={`input ${suggestedFields.has("product") ? suggestedRing : ""}`}
              value={form.product?.name ?? ""}
              onChange={(e) => {
                set("product", { ...form.product, name: e.target.value });
                clearSuggestedField("product");
              }}
              placeholder="Spring Espresso Bundle"
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              className={`input min-h-[80px] resize-none ${suggestedFields.has("product") ? suggestedRing : ""}`}
              value={form.product?.description ?? ""}
              onChange={(e) => {
                set("product", {
                  ...form.product,
                  description: e.target.value,
                });
                clearSuggestedField("product");
              }}
              placeholder="Key details, pricing, differentiators…"
            />
          </div>

          {/* Price point */}
          {(suggestedFields.has("product") && form.product?.pricePoint) ||
          form.product?.pricePoint ? (
            <div>
              <label className="label">Price point</label>
              <input
                className={`input ${suggestedFields.has("product") ? suggestedRing : ""}`}
                value={form.product?.pricePoint ?? ""}
                onChange={(e) => {
                  set("product", {
                    ...form.product,
                    pricePoint: e.target.value,
                  });
                  clearSuggestedField("product");
                }}
                placeholder="From $29/month"
              />
            </div>
          ) : null}

          {/* Differentiators */}
          {(form.product?.differentiators?.length ?? 0) > 0 && (
            <div>
              <label className="label">
                Key differentiators
                {suggestedFields.has("product") && (
                  <span className="ml-2 text-xs text-brand-600 font-normal">
                    ✦ AI suggested
                  </span>
                )}
              </label>
              <div className="space-y-2">
                {(form.product?.differentiators ?? []).map((d, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      className={`input flex-1 text-sm ${suggestedFields.has("product") ? suggestedRing : ""}`}
                      value={d}
                      onChange={(e) => {
                        const next = [...(form.product?.differentiators ?? [])];
                        next[i] = e.target.value;
                        set("product", {
                          ...form.product,
                          differentiators: next,
                        });
                        clearSuggestedField("product");
                      }}
                      placeholder={`Differentiator ${i + 1}`}
                    />
                    <button
                      onClick={() => {
                        const next = (
                          form.product?.differentiators ?? []
                        ).filter((_, j) => j !== i);
                        set("product", {
                          ...form.product,
                          differentiators: next,
                        });
                      }}
                      className="text-xs text-faint hover:text-red-500 px-1"
                      aria-label="Remove differentiator"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Platforms ── */}
        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="eyebrow">Platforms</h2>
            {brandId && (
              <Link
                to={`/settings/platforms?brandId=${brandId}`}
                className="text-xs text-brand-600 hover:text-brand-700"
              >
                Manage connections →
              </Link>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => {
              const isSelected = form.platforms?.includes(p.id as never);
              const isConnected = connectedProviders.has(p.provider);
              return (
                <button
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  aria-pressed={isSelected}
                  title={
                    isConnected
                      ? `${p.label} — account connected`
                      : `${p.label} — no account connected for this brand`
                  }
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    isSelected
                      ? "bg-brand-500 text-white border-brand-500"
                      : "border-gray-300 text-slate hover:border-brand-400"
                  }`}
                >
                  {p.label}
                  {isConnected ? (
                    <span
                      className={`ml-1.5 text-xs ${isSelected ? "text-white/70" : "text-green-500"}`}
                    >
                      ✓
                    </span>
                  ) : (
                    <span
                      className={`ml-1.5 text-xs ${isSelected ? "text-yellow-200" : "text-gray-300"}`}
                    >
                      ○
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {/* Warn if any selected platform has no connected account */}
          {form.platforms?.some(
            (pid) =>
              !connectedProviders.has(
                PLATFORMS.find((p) => p.id === pid)?.provider ??
                  ("" as SocialProvider),
              ),
          ) && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              One or more selected platforms don't have a connected account for
              this brand. Posts will be generated but can't be published until
              you{" "}
              <Link
                to={`/settings/platforms?brandId=${brandId}`}
                className="underline font-medium"
              >
                connect the account
              </Link>
              .
            </p>
          )}
        </div>

        {/* ── Key messages ── */}
        <div className="card p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="eyebrow">Key messages</h2>
              <p className="text-xs text-faint mt-0.5">
                Up to 5 core messages the AI will weave into posts.
              </p>
            </div>
            {suggestedFields.has("keyMessages") && (
              <span className="text-xs text-brand-600">✦ AI suggested</span>
            )}
          </div>

          {(form.keyMessages ?? [""]).slice(0, 5).map((msg, i) => (
            <input
              key={i}
              className={`input ${suggestedFields.has("keyMessages") ? suggestedRing : ""}`}
              value={msg}
              onChange={(e) => {
                setMessage(i, e.target.value);
                clearSuggestedField("keyMessages");
              }}
              placeholder={`Message ${i + 1}`}
            />
          ))}

          {(form.keyMessages?.length ?? 1) < 5 && (
            <button
              onClick={() =>
                set("keyMessages", [...(form.keyMessages ?? []), ""])
              }
              className="text-sm text-brand-600 hover:underline"
            >
              + Add message
            </button>
          )}
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="flex gap-3 mt-6 items-center">
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? "Saving…" : isNew ? "Create campaign" : "Save changes"}
        </button>
        <button
          onClick={() => navigate(`/brands/${brandId}/campaigns`)}
          className="btn-secondary"
        >
          Cancel
        </button>
        {isNew && (
          <p className="text-xs text-faint ml-1">
            After saving, click ⚡ Generate to create posts.
          </p>
        )}
      </div>

      {/* ── Stock image picker modal ── */}
      {showStockPicker && brandId && campaignId && (
        <StockImagePicker
          brandId={brandId}
          campaignId={campaignId}
          onImported={handleStockImageImported}
          onClose={() => setShowStockPicker(false)}
        />
      )}
    </div>
  );
}
